import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { LanguageService, ThemePreference, ThemeService } from '@core';

@Component({
  selector: 'app-theme-toggle',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule],
  styleUrl: './theme-toggle.component.scss',
  template: `
    @if (appearance() === 'mini-fab') {
      <button mat-mini-fab class="theme-toggle-mini-fab" (click)="toggleTheme()" [attr.aria-label]="ariaLabel()">
        <mat-icon>{{ themeIcon() }}</mat-icon>
      </button>
    } @else {
      <button mat-icon-button (click)="toggleTheme()" [attr.aria-label]="ariaLabel()">
        <mat-icon>{{ themeIcon() }}</mat-icon>
      </button>
    }
  `,
})
export class ThemeToggleComponent {
  private readonly theme = inject(ThemeService);
  private readonly languageService = inject(LanguageService);

  readonly appearance = input<'icon' | 'mini-fab'>('icon');
  readonly ariaLabel = computed(() => {
    const current = this.preferenceLabel(this.theme.preference());
    const next = this.preferenceLabel(this.nextPreference(this.theme.preference()));

    return this.languageService.t('layout.themeToggleLabel', { current, next });
  });

  themeIcon(): string {
    const icons: Record<ThemePreference, string> = {
      light: 'light_mode',
      dark: 'dark_mode',
      system: 'brightness_6',
    };
    return icons[this.theme.preference()];
  }

  toggleTheme(): void {
    this.theme.toggle();
  }

  private nextPreference(preference: ThemePreference): ThemePreference {
    const nextPreferences: Record<ThemePreference, ThemePreference> = {
      light: 'dark',
      dark: 'system',
      system: 'light',
    };
    return nextPreferences[preference];
  }

  private preferenceLabel(preference: ThemePreference): string {
    return this.languageService.t(`layout.theme.${preference}`);
  }
}
